/**
 * HOF-696 — the labour app's ONE data hook (D18, USE_CASE_APP_AUTHORING.md: one
 * data hook per app). It is the only file that talks to the API for the app:
 *
 *   reads (generic, D11)
 *     GET  /v1/farm/settings/labour/catalogue               the catalogue setting (D15)
 *     POST /v1/twin/entities/time_entry/query               own rows / one office cell
 *     POST /v1/twin/entities/work_day_report/query          own or every report
 *     POST /v1/twin/entities/time_entry/aggregate           the office week (D14)
 *     GET  /v1/farm/labour-access                           roster + access problems (D16)
 *   commands (D11)
 *     PUT    /v1/twin/labour/me/entries/{client_id}
 *     DELETE /v1/twin/labour/me/entries/{client_id}
 *     POST   /v1/twin/labour/me/days/{day}/copy-previous
 *     POST   /v1/twin/labour/me/days/{day}/confirm
 *     PUT    /v1/twin/labour/people/{person_id}/entries/{client_id}   on behalf (HOF-697, labour.write)
 *     DELETE /v1/twin/labour/people/{person_id}/entries/{client_id}
 *     POST   /v1/farm/labour-access/sync                     "Jetzt abgleichen" (D7)
 *
 * Every read is bounded by `attr_in work_day` (≤ 8 dates for a worker, 7 for
 * the office week) — the hook never lists a labour type unbounded (D14). The
 * server scopes a worker's reads to their own rows. A seat that may read the
 * whole team (a Vorarbeiter, HOF-697) is scoped by the seat's own `person_id`
 * in the query instead, so "my day" stays theirs. A command body never carries
 * a person (D12): an on-behalf command names it in the path.
 *
 * Response shapes the backend was still being built against are parsed
 * defensively in the `parse*` / `to*` functions below — that is where a
 * contract assumption lives, and the only place to change if it moves.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { twinService, type EntityListResponse } from '../services/twinService';

// ---------------------------------------------------------------- types

export interface CatalogueLabels {
  label_de: string;
  label_ro?: string;
  label_pl?: string;
}

export interface CatalogueActivity extends CatalogueLabels {
  key: string;
  activity_class?: string | null;
}

export type CostObjectKind = 'crop' | 'area';

export interface CatalogueCostObject extends CatalogueLabels {
  key: string;
  kind: CostObjectKind;
}

export interface LabourCatalogue {
  activities: CatalogueActivity[];
  costObjects: CatalogueCostObject[];
}

export interface LabourEntry {
  entityId: string;
  /** The phone's id for the row — what the PUT/DELETE commands address. */
  clientId: string | null;
  personId: string | null;
  workDay: string;
  activityKey: string;
  costObjectKind: CostObjectKind | null;
  costObjectKey: string;
  durationMin: number;
  note: string;
}

export interface WorkDayReport {
  personId: string | null;
  workDay: string;
  confirmed: boolean;
  confirmedAt: string | null;
  reflection: string;
}

/** D16 roster status: seat state, or the refusal reason of the D3 rule. */
export type RosterStatus =
  | 'signed_in'
  | 'pending'
  | 'ended'
  | 'not_employed'
  | 'no_phone'
  | 'phone_shared'
  | 'blocked'
  | 'not_in_policy'
  | 'office_seat'
  | 'legacy_invite'
  | 'manual_invite'
  | string;

export interface RosterPerson {
  personId: string;
  displayName: string;
  status: RosterStatus;
  /** The D3 reason behind the status (`eligible` or a refusal); a suspended seat keeps its cause here. */
  reason?: string;
  /** The server's verdict that the office can fix this (D16); absent on older answers. */
  problem?: boolean;
  /**
   * The Europe/Berlin date (`YYYY-MM-DD`) the person's worker seat was created (ui e2e): the days before
   * it are not "fehlt". Absent for a person with no seat, and on older answers (every past day counts).
   */
  accessSince?: string;
}

export interface EntryInput {
  workDay: string;
  activityKey: string;
  costObjectKey: string;
  durationMin: number;
  note: string;
}

export interface CopyPreviousResult {
  /** The earlier day the entries were copied from, when the server says. */
  sourceDay: string | null;
  /** How many entries were newly written, when the server says. */
  copied: number | null;
}

// ---------------------------------------------------------------- errors

export type LabourErrorKind =
  | 'access_ended' // 403 labour_access_ended — the seat no longer matches the card (D12)
  | 'app_not_active' // 403 app_not_active — the farm switched the app off (D6)
  | 'forbidden' // any other 403
  | 'busy' // 409 day_busy (or a 409 while UDB deploys) — retry
  | 'gone' // 410 — the client_id was tombstoned
  | 'day_cap' // 422 day_cap_exceeded — more than 16 h on a day
  | 'not_employed' // 422 person_not_employed — an on-behalf write for someone not employed (HOF-697)
  | 'invalid' // any other 422 / 400
  | 'unavailable' // 502/503/504 — e.g. Redis down for the day lease
  | 'network' // no response at all
  | 'unknown';

interface HttpishError {
  response?: { status?: number; data?: unknown };
  code?: string;
  message?: string;
}

/** The stable code of an error body: `detail` string, or `detail.reason|code|error`. */
export function errorCode(error: unknown): string | null {
  const data = (error as HttpishError)?.response?.data as Record<string, unknown> | string | undefined;
  if (typeof data === 'string') return data || null;
  if (!data || typeof data !== 'object') return null;
  const detail = data.detail;
  if (typeof detail === 'string') return detail;
  const candidates = [
    detail && typeof detail === 'object' ? (detail as Record<string, unknown>).reason : undefined,
    detail && typeof detail === 'object' ? (detail as Record<string, unknown>).code : undefined,
    detail && typeof detail === 'object' ? (detail as Record<string, unknown>).error : undefined,
    data.reason,
    data.code,
    data.error,
  ];
  const found = candidates.find((c) => typeof c === 'string' && c);
  return (found as string | undefined) ?? null;
}

export function classifyLabourError(error: unknown): LabourErrorKind {
  const err = error as HttpishError;
  const status = err?.response?.status;
  if (!err?.response || status === undefined) return 'network';
  const code = errorCode(error);
  if (status === 403) {
    if (code === 'labour_access_ended') return 'access_ended';
    if (code === 'app_not_active') return 'app_not_active';
    return 'forbidden';
  }
  if (status === 409) return 'busy';
  if (status === 410) return 'gone';
  if (status === 422 && code === 'day_cap_exceeded') return 'day_cap';
  if (status === 422 && code === 'person_not_employed') return 'not_employed';
  if (status === 400 || status === 422) return 'invalid';
  if (status === 502 || status === 503 || status === 504) return 'unavailable';
  return 'unknown';
}

export function isRetryableLabourError(kind: LabourErrorKind): boolean {
  return kind === 'busy' || kind === 'network' || kind === 'unavailable';
}

/** A refusal that means "this seat cannot use the app right now": the phone shows "Bitte im Büro melden". */
export function isAccessRefusal(kind: LabourErrorKind): boolean {
  return kind === 'access_ended' || kind === 'app_not_active';
}

/**
 * Automatic retries for retryable command failures (D13: `409 day_busy` and
 * network errors retry). Exported so suites can shorten the back-off.
 */
export const LABOUR_RETRY = { attempts: 3, baseDelayMs: 500 };

const retryPolicy = {
  // Offline, React Query would PAUSE the command ("Wird gesendet …" with no end, HOF-696 ui e2e);
  // 'always' runs it, so a dead connection fails into the kept draft and its "Erneut senden".
  networkMode: 'always' as const,
  retry: (failureCount: number, error: unknown) =>
    failureCount < LABOUR_RETRY.attempts && isRetryableLabourError(classifyLabourError(error)),
  retryDelay: (attempt: number) => LABOUR_RETRY.baseDelayMs * 2 ** attempt,
};

// ---------------------------------------------------------------- parsing

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

function toNumber(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : 0;
}

function toLabels(raw: Record<string, unknown>): CatalogueLabels {
  return {
    label_de: str(raw.label_de) || str(raw.key),
    label_ro: strOrNull(raw.label_ro) ?? undefined,
    label_pl: strOrNull(raw.label_pl) ?? undefined,
  };
}

/**
 * The catalogue setting (D15). The generic setting read returns the UDB
 * envelope `{namespace, key, value, version, ...}`; `value` is
 * `{activities: [...], cost_objects: [...]}`. A bare value is accepted too.
 */
export function parseCatalogue(raw: unknown): LabourCatalogue {
  const envelope = (raw ?? {}) as Record<string, unknown>;
  const value = (envelope.value && typeof envelope.value === 'object' ? envelope.value : envelope) as Record<
    string,
    unknown
  >;
  const activities = Array.isArray(value.activities) ? value.activities : [];
  const costObjects = Array.isArray(value.cost_objects) ? value.cost_objects : [];
  return {
    activities: activities
      .filter((a): a is Record<string, unknown> => !!a && typeof a === 'object' && !!str((a as Record<string, unknown>).key))
      .map((a) => ({ key: str(a.key), activity_class: strOrNull(a.activity_class), ...toLabels(a) })),
    costObjects: costObjects
      .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object' && !!str((c as Record<string, unknown>).key))
      .map((c) => ({ key: str(c.key), kind: c.kind === 'area' ? 'area' : 'crop', ...toLabels(c) })),
  };
}

/** A label in the reader's language, German when that translation is missing. */
export function catalogueLabel(item: CatalogueLabels | undefined, language: string): string {
  if (!item) return '';
  const base = language.split('-')[0];
  if (base === 'ro' && item.label_ro) return item.label_ro;
  if (base === 'pl' && item.label_pl) return item.label_pl;
  return item.label_de;
}

/** A flat `time_entry` row (pk as `entity_id`, attributes top-level). */
export function toEntry(row: Record<string, unknown>): LabourEntry {
  const kind = row.cost_object_kind;
  return {
    entityId: str(row.time_entry_id) || str(row.entity_id) || str(row.id),
    clientId: strOrNull(row.client_id),
    personId: strOrNull(row.person_id),
    workDay: str(row.work_day),
    activityKey: str(row.activity_key),
    costObjectKind: kind === 'crop' || kind === 'area' ? kind : null,
    costObjectKey: str(row.cost_object_key),
    durationMin: toNumber(row.duration_min),
    note: str(row.note),
  };
}

export function toReport(row: Record<string, unknown>): WorkDayReport {
  return {
    personId: strOrNull(row.person_id),
    workDay: str(row.work_day),
    confirmed: str(row.status) === 'confirmed',
    confirmedAt: strOrNull(row.confirmed_at),
    reflection: str(row.reflection),
  };
}

/** D16 roster: `{items: [...]}` (the member list's envelope), `{persons: [...]}` or a bare array. */
export function parseRoster(raw: unknown): RosterPerson[] {
  const envelope = raw as Record<string, unknown> | unknown[] | null;
  const list = Array.isArray(envelope)
    ? envelope
    : Array.isArray(envelope?.items)
      ? envelope.items
      : Array.isArray(envelope?.persons)
        ? envelope.persons
        : [];
  return (list as Record<string, unknown>[])
    .filter((p) => p && typeof p === 'object' && str(p.person_id))
    .map((p) => ({
      personId: str(p.person_id),
      displayName: str(p.display_name) || str(p.name) || str(p.person_id),
      status: str(p.status) || str(p.reason) || 'unknown',
      reason: str(p.reason) || undefined,
      problem: typeof p.problem === 'boolean' ? p.problem : undefined,
      accessSince: isoDateOnly(p.access_since),
    }));
}

/**
 * A date-only `YYYY-MM-DD`; anything else is `undefined` — read defensively. A timestamp is refused, not
 * cut: its UTC date can be the wrong Berlin day.
 */
function isoDateOnly(raw: unknown): string | undefined {
  return typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : undefined;
}

export function parseCopyPrevious(raw: unknown): CopyPreviousResult {
  const r = (raw ?? {}) as Record<string, unknown>;
  const copiedRaw = r.copied ?? r.created ?? r.written;
  return {
    sourceDay: strOrNull(r.source_day) ?? strOrNull(r.from_day),
    copied: typeof copiedRaw === 'number' ? copiedRaw : null,
  };
}

// ---------------------------------------------------------------- fetching

const PAGE_LIMIT = 500;
const PAGE_CAP = 10;

async function queryAllRows(
  entityType: 'time_entry' | 'work_day_report',
  body: { attr_in?: Record<string, string[]>; filters?: Record<string, string> },
): Promise<Record<string, unknown>[]> {
  const rows: Record<string, unknown>[] = [];
  for (let page = 1; page <= PAGE_CAP; page += 1) {
    const res: EntityListResponse = await twinService.queryEntities(entityType, { ...body, page, limit: PAGE_LIMIT });
    rows.push(...(res.entities ?? []));
    if (!res.has_more) break;
  }
  return rows;
}

const entryPath = (clientId: string) => `/v1/twin/labour/me/entries/${encodeURIComponent(clientId)}`;
const personEntryPath = (personId: string, clientId: string) =>
  `/v1/twin/labour/people/${encodeURIComponent(personId)}/entries/${encodeURIComponent(clientId)}`;
const dayPath = (day: string, verb: 'copy-previous' | 'confirm') =>
  `/v1/twin/labour/me/days/${encodeURIComponent(day)}/${verb}`;

export const labourKeys = {
  all: ['labour'] as const,
  catalogue: ['labour', 'catalogue'] as const,
  me: ['labour', 'me'] as const,
  myDays: (dates: readonly string[], personId: string | null = null) =>
    ['labour', 'me', 'days', personId ?? '', ...dates] as const,
  office: ['labour', 'office'] as const,
  officeWeek: (dates: readonly string[]) => ['labour', 'office', 'week', ...dates] as const,
  officeCell: (personId: string, day: string) => ['labour', 'office', 'cell', personId, day] as const,
  roster: ['labour', 'roster'] as const,
};

// ---------------------------------------------------------------- reads

/** Reads do not retry an access refusal; anything else gets React Query's default once. */
const readRetry = (failureCount: number, error: unknown) => {
  const kind = classifyLabourError(error);
  if (kind === 'forbidden' || isAccessRefusal(kind) || kind === 'invalid') return false;
  return failureCount < 2;
};

export function useLabourCatalogue(enabled = true) {
  return useQuery({
    queryKey: labourKeys.catalogue,
    queryFn: async () => parseCatalogue((await api.get('/v1/farm/settings/labour/catalogue')).data),
    enabled,
    staleTime: 5 * 60_000,
    retry: readRetry,
  });
}

export interface MyLabourDays {
  entries: LabourEntry[];
  reports: WorkDayReport[];
}

/**
 * The seat's own entries and reports for the given dates. With the seat's `personId` (HOF-697) the
 * read names it — a Vorarbeiter's seat may read every row — and anything else is dropped; without
 * it (an older backend) the server's own-rows scope is the bound, as for every worker.
 */
export function useMyLabourDays(dates: readonly string[], enabled = true, personId: string | null = null) {
  return useQuery({
    queryKey: labourKeys.myDays(dates, personId),
    queryFn: async (): Promise<MyLabourDays> => {
      const body = { attr_in: { work_day: [...dates] }, ...(personId ? { filters: { person_id: personId } } : {}) };
      const [entryRows, reportRows] = await Promise.all([
        queryAllRows('time_entry', body),
        queryAllRows('work_day_report', body),
      ]);
      const mine = <T extends { personId: string | null }>(row: T) => !personId || !row.personId || row.personId === personId;
      return { entries: entryRows.map(toEntry).filter(mine), reports: reportRows.map(toReport).filter(mine) };
    },
    enabled: enabled && dates.length > 0,
    retry: readRetry,
  });
}

export interface OfficeWeek {
  /** minutes[personId][workDay] */
  minutes: Record<string, Record<string, number>>;
  reports: WorkDayReport[];
  /** The aggregate's group cap fired — never render it as the whole week. */
  truncated: boolean;
}

/** The office week: one grouped SUM over the week + every report of the week (D11, D14). */
export function useOfficeWeek(dates: readonly string[], enabled = true) {
  return useQuery({
    queryKey: labourKeys.officeWeek(dates),
    queryFn: async (): Promise<OfficeWeek> => {
      const attrIn = { work_day: [...dates] };
      const [aggregate, reportRows] = await Promise.all([
        twinService.aggregateEntities('time_entry', {
          groupBy: ['person_id', 'work_day'],
          sumAttribute: 'duration_min',
          attrIn,
        }),
        queryAllRows('work_day_report', { attr_in: attrIn }),
      ]);
      const minutes: Record<string, Record<string, number>> = {};
      for (const item of aggregate.items ?? []) {
        const person = item.keys?.person_id;
        const day = item.keys?.work_day;
        if (!person || !day) continue;
        (minutes[person] ??= {})[day] = (minutes[person][day] ?? 0) + toNumber(item.sum);
      }
      return { minutes, reports: reportRows.map(toReport), truncated: Boolean(aggregate.truncated) };
    },
    enabled: enabled && dates.length > 0,
    retry: readRetry,
  });
}

/** One office cell: that person's entries for that day, read-only (D20). */
export function useOfficeCellEntries(personId: string | null, day: string | null) {
  return useQuery({
    queryKey: labourKeys.officeCell(personId ?? '', day ?? ''),
    queryFn: async () =>
      (await queryAllRows('time_entry', { filters: { person_id: personId!, work_day: day! } })).map(toEntry),
    enabled: Boolean(personId && day),
    retry: readRetry,
  });
}

export function useLabourRoster(enabled = true) {
  return useQuery({
    queryKey: labourKeys.roster,
    queryFn: async () => parseRoster((await api.get('/v1/farm/labour-access')).data),
    enabled,
    retry: readRetry,
  });
}

// ---------------------------------------------------------------- commands

export function useLabourCommands() {
  const queryClient = useQueryClient();
  const refreshMine = () => queryClient.invalidateQueries({ queryKey: labourKeys.me });

  const putEntry = useMutation({
    mutationFn: async ({ clientId, input }: { clientId: string; input: EntryInput }) => {
      // D12: the body names the work only — never person, tenant, actor, ids or provenance.
      const body: Record<string, unknown> = {
        work_day: input.workDay,
        activity_key: input.activityKey,
        cost_object_key: input.costObjectKey,
        duration_min: input.durationMin,
      };
      const note = input.note.trim();
      if (note) body.note = note;
      return (await api.put(entryPath(clientId), body)).data;
    },
    onSuccess: refreshMine,
    ...retryPolicy,
  });

  const deleteEntry = useMutation({
    mutationFn: async ({ clientId }: { clientId: string }) => (await api.delete(entryPath(clientId))).data,
    onSuccess: refreshMine,
    ...retryPolicy,
  });

  const copyPrevious = useMutation({
    mutationFn: async ({ day }: { day: string }) => parseCopyPrevious((await api.post(dayPath(day, 'copy-previous'))).data),
    onSuccess: refreshMine,
    ...retryPolicy,
  });

  const confirmDay = useMutation({
    mutationFn: async ({ day, reflection }: { day: string; reflection?: string }) => {
      // D9: an OMITTED reflection keeps the stored one; a present one — even "" — replaces it, so a
      // cleared field clears the stored reflection (codex r1 #5). Callers omit it only when the
      // field was never touched.
      const body = reflection === undefined ? {} : { reflection: reflection.trim() };
      return (await api.post(dayPath(day, 'confirm'), body)).data;
    },
    onSuccess: refreshMine,
    ...retryPolicy,
  });

  return { putEntry, deleteEntry, copyPrevious, confirmDay };
}

/**
 * HOF-697 — entering and correcting another person's time (`labour.write`: a Vorarbeiter, the
 * owner, the farm manager). Same body and rules as the worker's own commands; the person is in the
 * path. A new entry gets a fresh client id; an existing one is addressed by its row's `client_id`.
 * Success re-reads the office week and the open cell.
 */
export function useLabourOfficeCommands() {
  const queryClient = useQueryClient();
  const refreshOffice = () => queryClient.invalidateQueries({ queryKey: labourKeys.office });

  const putPersonEntry = useMutation({
    mutationFn: async ({ personId, clientId, input }: { personId: string; clientId: string; input: EntryInput }) => {
      const body: Record<string, unknown> = {
        work_day: input.workDay,
        activity_key: input.activityKey,
        cost_object_key: input.costObjectKey,
        duration_min: input.durationMin,
      };
      const note = input.note.trim();
      if (note) body.note = note;
      return (await api.put(personEntryPath(personId, clientId), body)).data;
    },
    onSuccess: refreshOffice,
    ...retryPolicy,
  });

  const deletePersonEntry = useMutation({
    mutationFn: async ({ personId, clientId }: { personId: string; clientId: string }) =>
      (await api.delete(personEntryPath(personId, clientId))).data,
    onSuccess: refreshOffice,
    ...retryPolicy,
  });

  return { putPersonEntry, deletePersonEntry };
}

/** "Jetzt abgleichen" (D7) — `members.manage` only; refreshes the roster. */
export function useLabourAccessSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post('/v1/farm/labour-access/sync')).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: labourKeys.roster }),
  });
}
